import * as React from 'react';
import { BaseRouteProps } from '../../../interfaces';
import { PVAPage, PVATeamSelect, LoadingIndicatorMini, PVABadge } from '../..';
import { CharacteristicDataService, GridOptionDataService, CharacteristicGroupDataService, ReportDataService } from '../../../services';
import { User, Team, Characteristic, GridOption, CharacteristicGroup, UserStyle } from '../../../models';
import { UserPermissions } from '../../../models';
import { UserStore, FilterStore } from '../../../stores';
import { Roles } from '../../../stores/Constants';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { MidRangeType } from '../../../enums';

interface ReadinessReport {
    scores: any[];
    configAverage: number;
    configStdDev: number;
    average: number;
    stdDev: number;
    teamAverage: number;
    teamStdDev: number;
    orgAverage: number;
    orgStdDev: number;
}

interface State {
    orgId: number;
    surveyId: number;
    surveyVersionId: number;
    selectedUserData: ReadinessReport;
    usersToFocus: User[];
    reportType: number;
    selectedCharacteristic: Characteristic | undefined;
    teams: Team[];
    team: Team | undefined;
    characteristics: Characteristic[];
    characteristicGroups: CharacteristicGroup[];
    miniLoad: boolean;
    gridOptions: GridOption[];
    listView: boolean;
    open: boolean;
    customScaleX: number;
    customScaleY: number;
    viewingUsers: number[];
    teamId: number;
    midRangeType: MidRangeType;
    stdDev: number;
}

interface Props extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class RFactorReport extends React.Component<Props, State> {
    private reportDataService = new ReportDataService(this.props.notificationService);
    private characteristicDataService = new CharacteristicDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private gridOptionDataService = new GridOptionDataService(this.props.notificationService);

    constructor(props: Props) {
        super(props);

        this.state = {
            selectedUserData: {
                scores: [],
                average: 0,
                stdDev: 0,
                teamAverage: 0,
                teamStdDev: 0,
                orgAverage: 0,
                orgStdDev: 0,
                configAverage: 0,
                configStdDev: 0,
            },
            usersToFocus: [],
            reportType: 0,
            orgId: 0,
            characteristics: [],
            characteristicGroups: [],
            surveyId: 0,
            surveyVersionId: 0,
            selectedCharacteristic: undefined,
            teams: [],
            team: undefined,
            miniLoad: false,
            gridOptions: [],
            listView: false,
            open: false,
            customScaleX: 0,
            customScaleY: 0,
            viewingUsers: [],
            teamId: 0,
            midRangeType: MidRangeType.Default,
            stdDev: 1.5,
        };
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get team() {
        if (this.props.FilterStore && this.props.FilterStore.team) {
            return this.props.FilterStore.team;
        }
        return null;
    }

    @computed get teams() {
        if (this.props.FilterStore && this.props.FilterStore.teams) {
            return this.props.FilterStore.teams;
        }
        return [];
    }

    @computed get users() {
        if (this.props.FilterStore && this.props.FilterStore.users) {
            return this.props.FilterStore.users;
        }
        return [];
    }

    @computed get teamId() {
        return this.props.FilterStore!.teamId;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }
    
    @computed get userCanSeeMidRanges() {
        return this.props.UserStore!.checkUserPermission(UserPermissions.RFactorReportMidRanges)
            || this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate]);
    }

    private updateSurveyVersion() {

        if (!this.surveyVersion) {
            return;
        }

        this.gridOptionDataService.getAllBySurveyVersion(this.surveyVersion, this.organization ? this.organization : undefined).then((gridOpts: GridOption[]) => {
            this.setState({
                gridOptions: gridOpts
            });
        });

        this.characteristicGroupDataService.getAllBySurveyVersion(this.surveyVersion).then((characteristicGroups: CharacteristicGroup[]) => {
            this.setState({
                characteristicGroups: characteristicGroups
            });
        });

        this.characteristicDataService.getAllBySurveyVersion(this.surveyVersion).then((characteristics: Characteristic[]) => {
            this.setState({
                characteristics: characteristics
            });

            if (this.surveyVersion && this.surveyVersion.readynessCharacteristicID) {
                var one = characteristics.find((x: Characteristic) => {
                    if (this.surveyVersion && this.surveyVersion.readynessCharacteristicID) {
                        return x.id === this.surveyVersion.readynessCharacteristicID;
                    }
                    return false;
                });
                if (one === undefined) {
                    return;
                }

                this.setState({
                    selectedCharacteristic: one
                });
            }
        });
    }

    private filterFirstUserScore() {
        const { selectedUserData } = this.state;

        const result: any[] = [];

        selectedUserData.scores.map((data: any) => {
            var ind = result.findIndex((x: any) => {
                return x.userID === data.userID;
            });
            if (ind === -1) {
                result.push(data);
            }
        });

        selectedUserData.scores = result;

        this.setState({
            selectedUserData
        });
    }

    private updateReport() {
        if (!this.survey || !this.surveyVersion) {
            return;
        }

        this.updateSurveyVersion();

        this.setState(
            {
                miniLoad: true
            }, 
            () => {
                // Load Readyness Data for surveyversion/team
                if (this.surveyVersion && this.teamId > 0) {
                    this.reportDataService.getReadynessReportByTeam(this.surveyVersion.id, this.teamId).then((readyness) => {
                        this.setState(
                            {
                                miniLoad: false,
                                selectedUserData: readyness,
                            }, 
                            this.filterFirstUserScore.bind(this));
                    });
                } else if (this.surveyVersion && this.teamId === -100 && this.organization) {
                    this.reportDataService.getReadynessReportByOrganization(this.surveyVersion.id, this.organization.id).then((readyness) => {
                        this.setState(
                            {
                                miniLoad: false,
                                selectedUserData: readyness,
                            }, 
                            this.filterFirstUserScore.bind(this));
                    });
                } else if (this.surveyVersion && this.teamId === -200 && this.organization) {
                    this.reportDataService.getReadynessReportByCandidates(this.surveyVersion.id, this.organization.id).then((readyness) => {
                        this.setState(
                            {
                                miniLoad: false,
                                selectedUserData: readyness,
                            }, 
                            this.filterFirstUserScore.bind(this));
                    });
                } else if (this.surveyVersion && this.teamId === -300 && this.organization) {
                    this.reportDataService.getReadynessReportByFormer(this.surveyVersion.id, this.organization.id).then((readyness) => {
                        this.setState(
                            {
                                miniLoad: false,
                                selectedUserData: readyness,
                            }, 
                            this.filterFirstUserScore.bind(this));
                    });
                } else {
                    this.setState({
                        miniLoad: false,
                        selectedUserData: {
                            scores: [],
                            average: 0,
                            stdDev: 0,
                            teamAverage: 0,
                            teamStdDev: 0,
                            orgAverage: 0,
                            orgStdDev: 0,
                            configAverage: 0,
                            configStdDev: 0,
                        },
                        viewingUsers: []
                    });
                }
            });
    }

    private update() {
        if (this.userRole !== Roles.User) {
            if (this.organization && this.organization.id !== this.state.orgId) {
                this.setState({
                    selectedUserData: {
                        scores: [],
                        average: 0,
                        stdDev: 0,
                        teamAverage: 0,
                        teamStdDev: 0,
                        orgAverage: 0,
                        orgStdDev: 0,
                        configAverage: 0,
                        configStdDev: 0,
                    },
                    miniLoad: false,
                    orgId: this.organization.id,
                    reportType: 0
                });
            }

            if (this.survey && this.state.surveyId !== this.survey.id) {
                this.setState(
                    {
                        surveyId: this.survey.id,
                        reportType: 0
                    },
                    this.updateReport);
            }

            if (this.surveyVersion && this.surveyVersion.id !== this.state.surveyVersionId) {
                this.setState(
                    {
                        surveyVersionId: this.surveyVersion.id,
                        reportType: 0
                    },
                    this.updateReport);                
            }

            if (this.teamId && this.teamId !== this.state.teamId) {
                this.setState(
                    {
                        teamId: this.teamId
                    },
                    this.updateReport);                
            }
        }
    }

    componentDidMount() {
        this.update();
    }

    componentWillReact() {
        this.update();
    }

    public teamChange(e: any) {
        var teamId: number = Number(e.target.value);
        if (teamId === 0) {
            this.setState(
                {
                    miniLoad: true
                },
                () => {
                    if (this.props.FilterStore) {
                        this.props.FilterStore.setTeamID(teamId).then(() => {
                            this.setState(
                                {
                                    team: undefined,
                                    miniLoad: false,
                                    selectedUserData: {
                                        scores: [],
                                        average: 0,
                                        stdDev: 0,
                                        teamAverage: 0,
                                        teamStdDev: 0,
                                        orgAverage: 0,
                                        orgStdDev: 0,
                                        configAverage: 0,
                                        configStdDev: 0,
                                    },
                                    viewingUsers: []
                                },
                                this.updateReport);
                        });
                    }
                });
            return;
        }
        var team = this.teams.find(x => x.id === teamId);
        if (team != null) {
            if (this.props.FilterStore) {
                this.setState(
                    {
                        miniLoad: true
                    }
                );
                this.props.FilterStore.setTeam(team).then(() => {
                    this.setState(
                        {
                            team: team,
                            selectedUserData: {
                                scores: [],
                                average: 0,
                                stdDev: 0,
                                teamAverage: 0,
                                teamStdDev: 0,
                                orgAverage: 0,
                                orgStdDev: 0,
                                configAverage: 0,
                                configStdDev: 0,
                            },
                            viewingUsers: []
                        },
                        this.updateReport);
                });
                this.props.FilterStore.teamId = Number(e.target.value);
            }
        } else {
            this.setState(
                {
                    miniLoad: true
                },
                () => {
                    if (this.props.FilterStore) {
                        this.props.FilterStore.setTeamID(teamId).then(() => {
                            this.setState(
                                {
                                    team: undefined,
                                    miniLoad: false,
                                    selectedUserData: {
                                        scores: [],
                                        average: 0,
                                        stdDev: 0,
                                        teamAverage: 0,
                                        teamStdDev: 0,
                                        orgAverage: 0,
                                        orgStdDev: 0,
                                        configAverage: 0,
                                        configStdDev: 0,
                                    },
                                    viewingUsers: []
                                },
                                this.updateReport);
                        });
                    }
                });
        }
    }

    private ActionsButton() {
        if (!this.userCanSeeMidRanges) {
            return <></>;
        }
        return (
            <div className="btn-group dropdown">
                <button type="button" className="btn btn-primary"  onClick={(e) => this.setState({open: !this.state.open })}>
                    <span>Actions </span>
                    <span className="caret" />
                </button>
                <ul className={`action-menu dropdown-menu ${this.state.open ? 'open' : ''}`}>
                    <li className="options-header">Range Options</li>
                    <li className="options-entry">      
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> Use Ranges From</span>
                        </div>              
                        <select className="form-control" onChange={(e) => { this.setState({midRangeType: parseInt(e.target.value, 10), open: !this.state.open}, this.forceUpdate); }}>
                            <option selected={this.state.midRangeType === MidRangeType.Default} value={MidRangeType.Default}>PVA</option>
                            {this.teamId > 0 && <option selected={this.state.midRangeType === MidRangeType.Team} value={MidRangeType.Team}>Current Team</option>}
                            <option selected={this.state.midRangeType === MidRangeType.Organization} value={MidRangeType.Organization}>Current Organization</option>
                        </select>
                    </li>
                    <li className="options-entry">      
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> Group</span>
                        </div>              
                        <select className="form-control" value={this.state.stdDev} onChange={(e) => { this.setState({ stdDev: parseFloat(e.target.value), open: !this.state.open}, this.forceUpdate); }}>
                            <option value={1.5}>1</option>
                            <option value={1.25}>2</option>
                            <option value={1.0}>3</option>
                            <option value={.5}>4</option>
                        </select>
                    </li>
                    
                </ul>
        </div>);
    }


    userSort(a: User, b: User) {
        if (a.lastName > b.lastName) {
            return 1;
        } else if (a.lastName < b.lastName) {
            return -1;
        }
        if (a.firstName > b.firstName) {
            return 1;
        } else if (a.firstName < b.firstName) {
            return -1;
        }
        return 0;
    }

    addHoverClass(userId: number) {
        this.users.findIndex(x => {
            return x.id === userId;
        });

        // if (userIndex >= 0) {
        //     // first remove pull-to-front from everyone
        //     data.forEach(x => x.additionalClass = '');

        //     // then set the new classes
        //     data[quadrantDataIndex].additionalClass = 'pull-to-front hovered-user';
        //     this.setState({data: data});
        // }
    }

    removeHoverClass(userId: number) {
        this.users.findIndex(x => {
            return x.id === userId;
        });

        // if (quadrantDataIndex >= 0) {
        //     data[quadrantDataIndex].additionalClass = 'pull-to-front';
        //     this.setState({data: data});
        // }
    }

    private addUser(u: User) {
        const viewingUsers = this.state.viewingUsers;
        if (viewingUsers.indexOf(u.id) === -1) {
            viewingUsers.push(u.id);
        }
        this.setState({
            viewingUsers
        });
    }

    private removeUser(u: User) {
        let viewingUsers = this.state.viewingUsers;
        viewingUsers = viewingUsers.filter(x => x !== u.id);
        this.setState({
            viewingUsers
        });
    }

    private selectAll() {
        this.setState({
            viewingUsers: this.users.map(x => x.id)
        });
    }

    private unselectAll() {
        this.setState({
            viewingUsers: []
        });
    }

    private PVABadge(user: User, index: number) {
        if (user === undefined) {
            return <PVABadge key={`pvabadge-${index}`} style={''} badge={''} />;
        }
        if (!this.survey || !user.userStyles || !this.surveyVersion) {
            return <PVABadge key={`pvabadge-${index}`} style={''} badge={''} />;
        }

        var surveyVersionId = this.surveyVersion.id;
        var userStyle = user.userStyles.find((us: UserStyle) => {
            return us.style.surveyVersionID === surveyVersionId;
        });

        if (userStyle) {
            return <PVABadge key={`pvabadge-${index}`} style={userStyle.style.name} badge={userStyle.style.name[0]} />;
        }

        if (!userStyle && user.userStyles.length > 0) {
            return <PVABadge key={`pvabadge-${index}`} style={user.userStyles[0].style.name} badge={user.userStyles[0].style.name[0]} />;
        }

        return <PVABadge key={`pvabadge-${index}`} style={''} badge={''} />;
    }

    public render() {
        const { midRangeType, selectedUserData, stdDev } = this.state;

        let defaultOptions: any[] = [{
            id: 0,
            value: '- Select -'
        }];
        
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllUsers) ) {
            defaultOptions.push({
                id: -100,
                value: 'All Users'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllCandidates) ) {
            defaultOptions.push({
                id: -200,
                value: 'All Candidates'
            });
        }
        if (!this.props.UserStore!.userRoleIn([Roles.Manager, Roles.User]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAllFormerEmployees) ) {
            defaultOptions.push({
                id: -300,
                value: 'All Former Employees'
            });
        }

        let lowScore: number = (selectedUserData.configAverage - selectedUserData.configStdDev * stdDev) || 27.5;
        let highScore: number = (selectedUserData.configAverage + selectedUserData.configStdDev * stdDev) || 80.4;

        switch (midRangeType) {
            case MidRangeType.All:
                lowScore = selectedUserData.average - selectedUserData.stdDev * stdDev;
                highScore = selectedUserData.average + selectedUserData.stdDev * stdDev;
                break;
            case MidRangeType.Organization:
                lowScore = selectedUserData.orgAverage - selectedUserData.orgStdDev * stdDev;
                highScore = selectedUserData.orgAverage + selectedUserData.orgStdDev * stdDev;
                break;
            case MidRangeType.Team:
                if (this.teamId > 0) {
                    lowScore = selectedUserData.teamAverage - selectedUserData.teamStdDev * stdDev;
                    highScore = selectedUserData.teamAverage + selectedUserData.teamStdDev * stdDev;
                }
                break;
            case MidRangeType.Default:
                break;
            default:
                break;
        }

        const lowUsers: User[] = [];
        selectedUserData.scores.filter((x: any) => this.state.viewingUsers.indexOf(x.userID) !== -1).filter((x: any) => x.score <= lowScore).map((x: any) => {
            var u = this.users.find(user => user.id === x.userID);
            if (u) {
                if (lowUsers.indexOf(u) === -1) {
                    lowUsers.push(u);
                }
            }
        });

        const midUsers: User[] = [];
        selectedUserData.scores.filter((x: any) => this.state.viewingUsers.indexOf(x.userID) !== -1).filter((x: any) => x.score > lowScore && x.score < highScore).map((x: any) => {
            var u = this.users.find(user => user.id === x.userID);
            if (u) {
                if (midUsers.indexOf(u) === -1) {
                    midUsers.push(u);
                }
            }
        });

        const highUsers: User[] = [];
        selectedUserData.scores.filter((x: any) => this.state.viewingUsers.indexOf(x.userID) !== -1).filter((x: any) => x.score >= highScore).map((x: any) => {
            var u = this.users.find(user => user.id === x.userID);
            if (u) {
                if (highUsers.indexOf(u) === -1) {
                    highUsers.push(u);
                }
            }
        });

        return (
            <PVAPage>
                {this.organization && <div id={`organization-${this.organization.id}`} />}
                {this.survey && <div id={`survey-${this.survey.id}`} />}

                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            <span>PVA R Factor Report<span className="trademark">TM</span></span>
                             <span className="light"> for </span>
                            <PVATeamSelect disabled={this.state.miniLoad} defaultOptions={defaultOptions} options={this.teams} selected={this.teamId} onChange={(e: any) => { this.teamChange(e); }} />
                            {this.state.miniLoad && <LoadingIndicatorMini />}
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                    <PVAPage.Header.Buttons>
                        {this.state.reportType !== -1 && this.ActionsButton()}
                    </PVAPage.Header.Buttons>
                </PVAPage.Header>

                <PVAPage.Body>
                    {this.teamId === 0 &&  <div className="pva-welcome">
                        <span>Please select a team</span>
                    </div>}
                    {(this.teamId > 0 || this.teamId < -1) && <div>
                        <div className="row">
                            <div className="col-xs-3">
                            
                                <div className="team-grid-data-list-wrapper">
                                    <div>
                                        <label>
                                            <span>Not Ready Now </span>
                                            {lowUsers.length > 0 && <span>({lowUsers.length})</span>}
                                        </label>
                                        <ul className="user-list">
                                            {
                                                lowUsers
                                                    .sort(this.userSort)
                                                    .map((user: User, index: number) => {
                                                        return <li key={`user-list-${user.id}`} className="person pva-badge-small" onMouseEnter={() => this.addHoverClass(user.id)} onMouseLeave={() => this.removeHoverClass(user.id)} onClick={() => this.removeUser(user)}>
                                                            <span className="text">{user.lastName}, {user.firstName}</span>
                                                            {this.PVABadge(user, index)}
                                                        </li>;
                                                    })
                                            }
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xs-3">
                                <div className="team-grid-data-list-wrapper">
                                    <div>
                                        <label>
                                            <span>Performers & High Performers </span>
                                            {midUsers.length > 0 && <span>({midUsers.length})</span>}
                                        </label>
                                        <ul className="user-list">
                                            {
                                                midUsers
                                                    .sort(this.userSort)
                                                    .map((user: User, index: number) => {
                                                        return <li key={`user-list-${user.id}`} className="person pva-badge-small" onMouseEnter={() => this.addHoverClass(user.id)} onMouseLeave={() => this.removeHoverClass(user.id)} onClick={() => this.removeUser(user)}>
                                                            <span className="text">{user.lastName}, {user.firstName}</span>
                                                            {this.PVABadge(user, index)}
                                                        </li>;
                                                    })
                                            }
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xs-3">
                                <div className="team-grid-data-list-wrapper">
                                    <div>
                                        <label>
                                            <span>Top of Class </span>
                                            {highUsers.length > 0 && <span>({highUsers.length})</span>}
                                        </label>
                                        <ul className="user-list">
                                            {
                                                highUsers
                                                    .sort(this.userSort)
                                                    .map((user: User, index: number) => {
                                                        return <li key={`user-list-${user.id}`} className="person pva-badge-small" onMouseEnter={() => this.addHoverClass(user.id)} onMouseLeave={() => this.removeHoverClass(user.id)} onClick={() => this.removeUser(user)}>
                                                            <span className="text">{user.lastName}, {user.firstName}</span>
                                                            {this.PVABadge(user, index)}
                                                        </li>;
                                                    })
                                            }
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xs-3">
                                <div className="team-grid-data-list-wrapper">
                                    <div>
                                        <label>Select Users</label>
                                        <a className="btn btn-primary btn-block" onClick={() => this.selectAll()}>Select All</a>
                                        <a className="btn btn-primary btn-block" onClick={() => this.unselectAll()}>Unselect All</a>
                                        <ul className="user-list">
                                            {
                                                this.users
                                                    .filter(x => this.state.viewingUsers.indexOf(x.id) === -1)
                                                    .sort(this.userSort)
                                                    .map((user: User, index: number) => {
                                                        return <li key={`user-${user.id}`} className="person pva-badge-small" onMouseEnter={() => this.addHoverClass(user.id)} onMouseLeave={() => this.removeHoverClass(user.id)} onClick={() => this.addUser(user)}>
                                                            <span className="text">{user.lastName}, {user.firstName}</span>
                                                            {this.PVABadge(user, index)}
                                                        </li>;
                                                    })
                                            }
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>}
                </PVAPage.Body>
                <PVAPage.Footer />
            </PVAPage>);
    }
}


// WEBPACK FOOTER //
// ./src/components/reports/rfactor/RFactorReport.tsx